import { Button, DropdownMenu, Icon } from '@dittolive/anvil'
import { ArrowRightIcon, CaretDownIcon, PlusIcon } from '@phosphor-icons/react'
import graphql from 'babel-plugin-relay/macro'
import { useAppToDatabaseRename } from 'components/hooks'
import { TenantContext } from 'components/tenant'
import paths, { isStaticPath } from 'paths'
import { Suspense, useContext, useEffect, useState } from 'react'
import { useLazyLoadQuery } from 'react-relay/hooks'
import { useLocation, useNavigate } from 'react-router-dom'

import { AppTenantMenuQuery } from './__generated__/AppTenantMenuQuery.graphql'
import OrganizationAppsMenuItem from './OrganizationAppsMenuItem'
import OrganizationsMenuItem from './OrganizationsMenuItem'
import PersonalAppsMenuItem from './PersonalAppsMenuItem'
import RecentOrganizationAppsMenuItem from './RecentOrganizationAppsMenuItem'

const appTenantQuery = graphql`
  query AppTenantMenuQuery($slug: String!, $skipOrganization: Boolean!) {
    viewer {
      organization(slug: $slug) @skip(if: $skipOrganization) {
        # eslint-disable-next-line relay/unused-fields
        id
        name
      }
    }
  }
`

export default function AppTenantMenu() {
  const { tenant, updateTenant, getTenantHome } = useContext(TenantContext)
  const skipOrganization = !tenant

  const { viewer } = useLazyLoadQuery<AppTenantMenuQuery>(appTenantQuery, {
    slug: tenant || '',
    skipOrganization,
  })

  const [isOpen, setIsOpen] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  const activeTenant = viewer.organization?.name || 'Personal'

  const { databasesText, titlePluralText, isRenamed } = useAppToDatabaseRename()

  const handleSwitchTenant = (tenant: string | null) => {
    const redirectPath = getTenantHome(tenant)

    // NOTE: this step is important, otherwise if we route to /, we will be redirected back
    // here since the tenant wasn't updated.
    updateTenant(tenant)

    const pathname = location.pathname
    const shouldRedirect = !isStaticPath(pathname, isRenamed)
    if (shouldRedirect) {
      navigate(redirectPath)
    }
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild>
        <Button
          data-testid="appTenantMenuTrigger"
          className="border-border-normal bg-background-surface text-foreground-normal enabled:hover:bg-background-surface focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 group flex h-8 max-w-full flex-initial appearance-none items-center justify-between gap-1 rounded-md border py-2 pl-3 pr-2 text-left font-sans text-base font-normal normal-case tracking-normal outline-none"
        >
          <span className="truncate whitespace-nowrap">{activeTenant}</span>
          <Icon
            className="text-foreground-subtle group-hover:text-foreground-normal group-focus-visible:text-foreground-normal size-4 flex-none"
            svg={<CaretDownIcon />}
          />
        </Button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content
        align="start"
        className="sm:min-w-90 max-h-[calc(100vh-96px)] overflow-y-auto sm:max-h-[calc(100vh-72px)]"
      >
        <DropdownMenu.Label>{titlePluralText}</DropdownMenu.Label>
        {tenant && (
          <Suspense fallback={null}>
            <OrganizationAppsMenuItem isOpen={isOpen} slug={tenant} />
          </Suspense>
        )}
        {!tenant && (
          <Suspense fallback={null}>
            <PersonalAppsMenuItem isOpen={isOpen} />
          </Suspense>
        )}
        <DropdownMenu.Separator />
        <Suspense fallback={null}>
          <RecentOrganizationAppsMenuItem isOpen={isOpen} />
        </Suspense>

        <DropdownMenu.Separator />

        <DropdownMenu.Label>Organizations</DropdownMenu.Label>
        <Suspense fallback={null}>
          <OrganizationsMenuItem
            isOpen={isOpen}
            onChangeTenant={handleSwitchTenant}
          />
        </Suspense>

        <DropdownMenu.Separator />

        <DropdownMenu.Item
          onSelect={() => navigate(paths.userOrganizations({}))}
          className="justify-between"
        >
          More organizations & {databasesText}
          <Icon
            className="text-foreground-subtle ml-2 size-4"
            svg={<ArrowRightIcon />}
          />
        </DropdownMenu.Item>

        <DropdownMenu.Separator />

        <DropdownMenu.Item
          onSelect={() => navigate(paths.createOrganization())}
          className="justify-between"
        >
          New organization
          <Icon
            className="text-foreground-subtle ml-2 size-4"
            svg={<PlusIcon />}
          />
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
